import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, MessageSquare, Save } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { PageLayout } from '@/components/ui/PageLayout';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { TABLE_CELL, TABLE_CELL_MUTED, TABLE_HEADER } from '@/components/ui/pageChrome';
import { formatBalance } from '@/hooks/useSms';
import {
  useAdjustSmsCreditAsAdmin,
  useCancelSmsBundleOrder,
  useFulfillSmsBundleOrder,
  usePlatformSmsOverview,
  useSetSmsSegmentPrice,
  useSmsBundleOrders,
  useSmsBundleProducts,
  useUpsertSmsBundleProduct,
  type PlatformSmsClinicRow,
  type SmsBundleProduct,
} from '@/hooks/useSmsBilling';
import { remainingSmsSegments, SMS_DEFAULT_PRICE_DKK_CENTS } from '@/lib/smsCompose';
import { centsToDkkInput, parseDkkInputToCents } from '@/hooks/usePlatformBilling';

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
}

export function AdminSms() {
  const { t, i18n } = useTranslation();
  const overview = usePlatformSmsOverview();
  const bundles = useSmsBundleProducts();
  const setPrice = useSetSmsSegmentPrice();
  const upsertBundle = useUpsertSmsBundleProduct();
  const fulfill = useFulfillSmsBundleOrder();
  const cancelOrder = useCancelSmsBundleOrder();
  const adjust = useAdjustSmsCreditAsAdmin();

  const [priceDraft, setPriceDraft] = useState<string | null>(null);
  const [selectedClinicId, setSelectedClinicId] = useState<string | null>(null);
  const [editingBundle, setEditingBundle] = useState<Partial<SmsBundleProduct> | null>(null);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustNote, setAdjustNote] = useState('');
  const [fulfillNote, setFulfillNote] = useState('');

  const orders = useSmsBundleOrders(selectedClinicId);
  const priceCents = overview.data?.price_per_segment_cents ?? SMS_DEFAULT_PRICE_DKK_CENTS;
  const clinics = overview.data?.clinics ?? [];
  const selectedClinic = clinics.find((c) => c.clinic_id === selectedClinicId) ?? null;
  const locale = i18n.language.startsWith('da') ? 'da' : 'en';

  const boughtClinics = useMemo(
    () => clinics.filter((c) => c.lifetime_purchased_cents > 0 || c.paid_orders > 0 || c.balance_cents > 0),
    [clinics],
  );

  async function savePrice(e: FormEvent) {
    e.preventDefault();
    const cents = parseDkkInputToCents(priceDraft ?? centsToDkkInput(priceCents));
    if (cents === null || cents <= 0) {
      toast.error(t('admin.sms.invalid_price'));
      return;
    }
    try {
      await setPrice.mutateAsync(cents);
      toast.success(t('admin.sms.price_saved'));
      setPriceDraft(null);
    } catch {
      toast.error(t('admin.sms.save_error'));
    }
  }

  async function saveBundle(e: FormEvent) {
    e.preventDefault();
    if (!editingBundle) return;
    const segments = Number(editingBundle.segments);
    const price = Number(editingBundle.price_cents);
    if (!editingBundle.name_da?.trim() || !Number.isFinite(segments) || segments <= 0 || !Number.isFinite(price) || price < 0) {
      toast.error(t('admin.sms.invalid_bundle'));
      return;
    }
    try {
      await upsertBundle.mutateAsync({
        id: editingBundle.id ?? null,
        slug: editingBundle.slug?.trim() || slugify(editingBundle.name_da),
        name_da: editingBundle.name_da.trim(),
        name_en: (editingBundle.name_en ?? editingBundle.name_da).trim(),
        segments,
        price_cents: price,
        is_active: editingBundle.is_active !== false,
        sort_order: editingBundle.sort_order ?? 0,
      });
      toast.success(t('admin.sms.bundle_saved'));
      setEditingBundle(null);
    } catch {
      toast.error(t('admin.sms.save_error'));
    }
  }

  async function markPaid(orderId: string) {
    try {
      await fulfill.mutateAsync({ order_id: orderId, note: fulfillNote.trim() || undefined });
      toast.success(t('admin.sms.marked_paid'));
      setFulfillNote('');
    } catch {
      toast.error(t('admin.sms.save_error'));
    }
  }

  async function markCancelled(orderId: string) {
    try {
      await cancelOrder.mutateAsync({ order_id: orderId, note: fulfillNote.trim() || undefined });
      toast.success(t('admin.sms.marked_cancelled'));
    } catch {
      toast.error(t('admin.sms.save_error'));
    }
  }

  async function grantCredit(e: FormEvent) {
    e.preventDefault();
    if (!selectedClinicId) return;
    const trimmed = adjustAmount.trim();
    const negative = trimmed.startsWith('-');
    const centsAbs = parseDkkInputToCents(negative ? trimmed.slice(1) : trimmed);
    if (centsAbs === null || centsAbs === 0) {
      toast.error(t('admin.sms.invalid_adjust'));
      return;
    }
    const cents = negative ? -centsAbs : centsAbs;
    if (!adjustNote.trim()) {
      toast.error(t('admin.sms.note_required'));
      return;
    }
    try {
      await adjust.mutateAsync({
        clinic_id: selectedClinicId,
        delta_cents: cents,
        reason: adjustNote.trim(),
      });
      toast.success(t('admin.sms.adjust_saved'));
      setAdjustAmount('');
      setAdjustNote('');
    } catch {
      toast.error(t('admin.sms.save_error'));
    }
  }

  return (
    <PageLayout title={t('admin.sms.title')} subtitle={t('admin.sms.subtitle')}>
      {overview.error && <ErrorAlert message={t('admin.sms.load_error')} />}

      <section className="rounded-lg border border-ds-line bg-ds-card p-6">
        <h2 className="text-lg font-semibold text-ds-text">{t('admin.sms.price_title')}</h2>
        <p className="mt-1 text-sm text-ds-muted">{t('admin.sms.price_desc')}</p>
        <form className="mt-4 flex flex-wrap items-end gap-3" onSubmit={savePrice}>
          <Input
            label={t('admin.sms.price_per_segment')}
            type="text"
            inputMode="decimal"
            value={priceDraft ?? centsToDkkInput(priceCents)}
            onChange={(e) => setPriceDraft(e.target.value)}
            className="w-40"
            trailingIcon={<span className="text-sm">kr</span>}
          />
          <Button type="submit" size="sm" disabled={setPrice.isPending || priceDraft === null}>
            {setPrice.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
            {t('admin.sms.save')}
          </Button>
        </form>
      </section>

      <section className="mt-6 rounded-lg border border-ds-line bg-ds-card p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-ds-text">{t('admin.sms.bundles_title')}</h2>
            <p className="mt-1 text-sm text-ds-muted">{t('admin.sms.bundles_desc')}</p>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() =>
              setEditingBundle({
                name_da: '',
                name_en: '',
                slug: '',
                segments: 100,
                price_cents: priceCents * 100,
                is_active: true,
                sort_order: 40,
              })
            }
          >
            {t('admin.sms.new_bundle')}
          </Button>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full divide-y divide-ds-line">
            <thead className="bg-ds-sunk">
              <tr>
                <th className={`${TABLE_HEADER} text-left`}>{t('admin.sms.col_bundle')}</th>
                <th className={`${TABLE_HEADER} text-left`}>{t('admin.sms.col_segments')}</th>
                <th className={`${TABLE_HEADER} text-left`}>{t('admin.sms.col_price')}</th>
                <th className={`${TABLE_HEADER} text-left`}>{t('admin.sms.col_active')}</th>
                <th className={`${TABLE_HEADER} text-left`} />
              </tr>
            </thead>
            <tbody className="divide-y divide-ds-line">
              {(bundles.data ?? []).map((bundle) => (
                <tr key={bundle.id}>
                  <td className={TABLE_CELL}>{locale === 'da' ? bundle.name_da : bundle.name_en}</td>
                  <td className={TABLE_CELL}>{bundle.segments}</td>
                  <td className={TABLE_CELL}>{formatBalance(bundle.price_cents)}</td>
                  <td className={TABLE_CELL_MUTED}>
                    {bundle.is_active ? t('admin.sms.active') : t('admin.sms.inactive')}
                  </td>
                  <td className={TABLE_CELL}>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setEditingBundle(bundle)}>
                      {t('admin.sms.edit')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {editingBundle && (
          <form className="mt-6 grid gap-4 rounded-lg border border-ds-line bg-ds-sunk p-5 md:grid-cols-2" onSubmit={saveBundle}>
            <Input
              label={t('admin.sms.bundle_name_da')}
              value={editingBundle.name_da ?? ''}
              onChange={(e) => setEditingBundle((prev) => ({ ...prev, name_da: e.target.value }))}
              required
            />
            <Input
              label={t('admin.sms.bundle_name_en')}
              value={editingBundle.name_en ?? ''}
              onChange={(e) => setEditingBundle((prev) => ({ ...prev, name_en: e.target.value }))}
            />
            <Input
              label={t('admin.sms.col_segments')}
              type="number"
              min={1}
              value={editingBundle.segments ?? ''}
              onChange={(e) => setEditingBundle((prev) => ({ ...prev, segments: Number(e.target.value) }))}
              required
            />
            <Input
              label={t('admin.sms.col_price')}
              type="text"
              inputMode="decimal"
              value={centsToDkkInput(editingBundle.price_cents ?? 0)}
              onChange={(e) => {
                const cents = parseDkkInputToCents(e.target.value);
                setEditingBundle((prev) => ({ ...prev, price_cents: cents ?? 0 }));
              }}
              trailingIcon={<span className="text-sm">kr</span>}
            />
            <label className="flex items-center gap-2 text-sm text-ds-text md:col-span-2">
              <input
                type="checkbox"
                checked={editingBundle.is_active !== false}
                onChange={(e) => setEditingBundle((prev) => ({ ...prev, is_active: e.target.checked }))}
                className="h-4 w-4 rounded border-ds-field text-ds-teal"
              />
              {t('admin.sms.active')}
            </label>
            <div className="flex gap-2 md:col-span-2">
              <Button type="submit" disabled={upsertBundle.isPending}>
                {t('admin.sms.save')}
              </Button>
              <Button type="button" variant="secondary" onClick={() => setEditingBundle(null)}>
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        )}
      </section>

      <section className="mt-6 rounded-lg border border-ds-line bg-ds-card p-6">
        <h2 className="text-lg font-semibold text-ds-text">{t('admin.sms.clinics_title')}</h2>
        <p className="mt-1 text-sm text-ds-muted">{t('admin.sms.clinics_desc')}</p>
        {overview.isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-ds-muted" />
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-ds-line">
              <thead className="bg-ds-sunk">
                <tr>
                  <th className={`${TABLE_HEADER} text-left`}>{t('admin.sms.col_clinic')}</th>
                  <th className={`${TABLE_HEADER} text-left`}>{t('admin.sms.col_bought')}</th>
                  <th className={`${TABLE_HEADER} text-left`}>{t('admin.sms.col_used')}</th>
                  <th className={`${TABLE_HEADER} text-left`}>{t('admin.sms.col_remaining')}</th>
                  <th className={`${TABLE_HEADER} text-left`}>{t('admin.sms.col_orders')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ds-line">
                {clinics.map((clinic) => (
                  <ClinicRow
                    key={clinic.clinic_id}
                    clinic={clinic}
                    priceCents={priceCents}
                    selected={selectedClinicId === clinic.clinic_id}
                    onSelect={() => setSelectedClinicId(clinic.clinic_id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {boughtClinics.length === 0 && !overview.isLoading && (
          <p className="mt-4 text-sm text-ds-muted">{t('admin.sms.no_purchases')}</p>
        )}
      </section>

      {selectedClinic && (
        <section className="mt-6 rounded-lg border border-ds-line bg-ds-card p-6">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-ds-teal" />
            <h2 className="text-lg font-semibold text-ds-text">{selectedClinic.clinic_name}</h2>
          </div>
          <p className="mt-1 text-sm text-ds-muted">{t('admin.sms.history_desc')}</p>

          <form className="mt-5 grid gap-3 rounded-lg border border-ds-line bg-ds-sunk p-4 md:grid-cols-[1fr_2fr_auto]" onSubmit={grantCredit}>
            <Input
              label={t('admin.sms.adjust_amount')}
              value={adjustAmount}
              onChange={(e) => setAdjustAmount(e.target.value)}
              placeholder="250,00"
              trailingIcon={<span className="text-sm">kr</span>}
            />
            <Input
              label={t('admin.sms.adjust_note')}
              value={adjustNote}
              onChange={(e) => setAdjustNote(e.target.value)}
              required
            />
            <div className="flex items-end">
              <Button type="submit" disabled={adjust.isPending}>
                {t('admin.sms.grant')}
              </Button>
            </div>
          </form>

          <Input
            className="mt-4 max-w-md"
            label={t('admin.sms.fulfill_note')}
            value={fulfillNote}
            onChange={(e) => setFulfillNote(e.target.value)}
          />

          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-ds-line">
              <thead className="bg-ds-sunk">
                <tr>
                  <th className={`${TABLE_HEADER} text-left`}>{t('sms.col_when')}</th>
                  <th className={`${TABLE_HEADER} text-left`}>{t('admin.sms.col_segments')}</th>
                  <th className={`${TABLE_HEADER} text-left`}>{t('admin.sms.col_price')}</th>
                  <th className={`${TABLE_HEADER} text-left`}>{t('sms.col_status')}</th>
                  <th className={`${TABLE_HEADER} text-left`}>{t('sms.col_reason')}</th>
                  <th className={`${TABLE_HEADER} text-left`} />
                </tr>
              </thead>
              <tbody className="divide-y divide-ds-line">
                {(orders.data ?? []).map((order) => (
                  <tr key={order.id}>
                    <td className={TABLE_CELL}>
                      {new Date(order.created_at).toLocaleString('da-DK', { timeZone: 'Europe/Copenhagen' })}
                    </td>
                    <td className={TABLE_CELL}>{order.segments}</td>
                    <td className={TABLE_CELL}>{formatBalance(order.price_cents)}</td>
                    <td className={TABLE_CELL}>{t(`admin.sms.order_${order.status}`)}</td>
                    <td className={TABLE_CELL_MUTED}>{order.note ?? '—'}</td>
                    <td className={TABLE_CELL}>
                      {order.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button type="button" size="sm" onClick={() => void markPaid(order.id)} disabled={fulfill.isPending}>
                            {t('admin.sms.mark_paid')}
                          </Button>
                          <Button type="button" size="sm" variant="secondary" onClick={() => void markCancelled(order.id)}>
                            {t('common.cancel')}
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(orders.data ?? []).length === 0 && (
              <p className="mt-3 text-sm text-ds-muted">{t('admin.sms.no_orders')}</p>
            )}
          </div>
        </section>
      )}
    </PageLayout>
  );
}

function ClinicRow({
  clinic,
  priceCents,
  selected,
  onSelect,
}: {
  clinic: PlatformSmsClinicRow;
  priceCents: number;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const remaining = remainingSmsSegments(clinic.balance_cents, priceCents);
  return (
    <tr
      className={selected ? 'bg-ds-teal-tint' : 'cursor-pointer hover:bg-ds-neutral-tint'}
      onClick={onSelect}
    >
      <td className={TABLE_CELL}>
        <div className={`font-medium text-ds-text ${clinic.parent_clinic_id ? 'pl-4' : ''}`}>
          {clinic.clinic_name}
        </div>
        <div className={`text-xs text-ds-muted ${clinic.parent_clinic_id ? 'pl-4' : ''}`}>
          {clinic.parent_clinic_id
            ? `${t('admin.sms.subclinic_of', { parent: clinic.parent_clinic_name ?? '—' })} · `
            : ''}
          {clinic.sms_enabled ? t('sms.enabled') : t('sms.disabled')}
          {!clinic.module_enabled ? ` · ${t('admin.sms.module_off')}` : ''}
        </div>
      </td>
      <td className={TABLE_CELL}>
        {clinic.lifetime_purchased_segments} {t('sms.segments')}
        <div className="text-xs text-ds-muted">{formatBalance(clinic.lifetime_purchased_cents)}</div>
      </td>
      <td className={TABLE_CELL}>
        {clinic.lifetime_used_messages} {t('admin.sms.messages')}
        <div className="text-xs text-ds-muted">
          {clinic.lifetime_used_segments} {t('sms.segments')}
        </div>
      </td>
      <td className={TABLE_CELL}>
        {remaining} {t('sms.segments')}
        <div className="text-xs text-ds-muted">{formatBalance(clinic.balance_cents)}</div>
      </td>
      <td className={TABLE_CELL_MUTED}>
        {clinic.paid_orders} {t('admin.sms.paid')}
        {clinic.pending_orders > 0 ? ` · ${clinic.pending_orders} ${t('admin.sms.pending')}` : ''}
      </td>
    </tr>
  );
}
